<template>
<div id="cards-container">
  <!-- Loading overlay for entire card container -->
  <transition name="loading-fade">
    <div class="cards-loading-overlay" v-if="isLoading">
      <div class="loading-spinner"></div>
      <p>Loading cards</p>
    </div>
  </transition>
  
  <div class="cards-wrapper">
        <div 
          v-for="(card, index) in gameStore.cards"
          :key="card ? card.id : `slot-${index}`"
          class="card-slot"
          :data-index="index"
        >
          <GameCard
            v-if="card"
            :title="card.title"
            :content="card.content"
            :ranking="card.ranking"
            :explanation="card.explanation"
            :department="card.department"
            :frontImage="card.frontImage"
    
            :primaryColor="card.primaryColor"
            :secondaryColor="card.secondaryColor"
            :isFlipped="card.isFlipped"
            :isSelected="selectedCards.includes(card)"
            :selectable="gameStore.selectionEnabled"
            :isFalling="card.isFalling"
            :isAnimatingOut="card.isAnimatingOut"
            :isAnimatingIn="card.isAnimatingIn"
            :reelDelayMs="card.reelDelayMs || 0"
            :draggable="gameStore.gameStarted" 
            :class="{ 
              'card-moving': movingCardId === card.id,
              'slot-reel-out': card.isAnimatingOut,
              'slot-reel-in': card.isAnimatingIn
            }"
            @dragstart="handleDragStart($event, index)"
            @dragover="handleDragOver($event)"
            @drop="handleDrop($event, index)"
            @show-explanation="handleShowExplanation" 
            @select="handleSelect(card)"
          />
        </div>
      </div>
    
    <div id="priority-labels">
      <span class="higher"><i class="fa-solid fa-arrow-left"></i>Higher priority</span>
      <span class="lower">Lower priority<i class="fa-solid fa-arrow-right"></i></span>
    </div>

    <div class="message" v-if="gameStore.started && gameStore.noMoreCards">Sorry, we are out of cards!</div>

    <transition name="prompt-fade" mode="out-in">
      <div id="prompt-container" v-if="gameStore.gameStarted && gameStore.showPrompts">
        <div id="prompts" :key="gameStore.roundNumber">
          <div class="prompt-text">
            {{ promptMessage }}
          </div>
        </div>
      </div>
    </transition>


  </div>

      <!-- Explanation Modal using your existing SettingsModal component -->


</template>

<script setup>
import { useGameStore } from '@/stores/gameStore';
import GameCard from './GameCard.vue';
import { computed, ref, nextTick, watch } from 'vue';

const promptMessage = computed(() => {
  if (!gameStore.gameStarted) {
    return '';
  }

  if (!gameStore.showPrompts) {
    return '';
  }
  
  // If job dropping is enabled (e.g., after "I'm Done"), instruct the user to drag cards.
  if (gameStore.allowJobDrop) {
    return 'Select 3 cards to remove. New cards drop into those gaps, then the row shuffles and flips.';
  }
  
  // Special prompt for round 2 (first round after initial cards)
  if (gameStore.roundNumber === 2) {
    return "Great! You've completed your first round. The game will continue until all cards are dealt or the timer runs out.";
  }
  
  // Default prompt for round 1 and other rounds
  return "Re-arrange the cards in order of priority, highest to the left. Click I'M DONE when you are happy.";
});

const gameStore = useGameStore();

// Loading state for card container
const isLoading = ref(false);
const loadedImages = ref(new Set());

// Track if we've already scheduled prompt hiding for round 2
const round2TimeoutSet = ref(false);

// Show loader immediately when department is selected
watch(() => gameStore.selectedDepartment, (newDepartment) => {
  if (newDepartment) {
    // Start loading immediately when department is selected
    isLoading.value = true;
    loadedImages.value.clear();
  }
}, { immediate: true });

// Watch for cards changing to manage loading state
watch(() => gameStore.cards, (newCards) => {
  if (newCards && newCards.length > 0) {
    // Cards have been loaded, now check images
    const cardsWithImages = newCards.filter(card => card && card.frontImage);
    
    if (cardsWithImages.length === 0) {
      // No images to load, hide loading gracefully
      setTimeout(() => {
        isLoading.value = false;
      }, 100); // Brief delay for smooth transition
    } else {
      // Set up image loading tracking
      cardsWithImages.forEach(card => {
        const img = new Image();
        img.onload = () => {
          loadedImages.value.add(card.id);
          checkAllImagesLoaded();
        };
        img.onerror = () => {
          loadedImages.value.add(card.id);
          checkAllImagesLoaded();
        };
        img.src = card.frontImage;
      });
    }
  }
}, { immediate: true });

// Check if all images are loaded
const checkAllImagesLoaded = () => {
  const cardsWithImages = gameStore.cards.filter(card => card && card.frontImage);
  if (loadedImages.value.size >= cardsWithImages.length) {
    // All images loaded, hide loading gracefully
    setTimeout(() => {
      isLoading.value = false;
    }, 200); // Brief delay for smooth transition
  }
};

// Watch for round 2 and hide prompts after showing the message
watch(() => [gameStore.roundNumber, gameStore.showPrompts], ([newRound, showPrompts]) => {
  if (newRound === 2 && showPrompts && !round2TimeoutSet.value) {
    console.log('Round 2 detected, hiding prompts in 3 seconds...');
    round2TimeoutSet.value = true;
    // Hide prompts after 3 seconds when round 2 starts
    setTimeout(() => {
      console.log('Hiding prompts now and disabling them permanently');
      gameStore.showPrompts = false;
      gameStore.promptsDisabledAfterRound2 = true;
    }, 3000);
  }
  
  // Reset the flag if we're no longer in round 2
  if (newRound !== 2) {
    round2TimeoutSet.value = false;
  }
});

// Track which card is being moved
const movingCardId = ref(null);

// FLIP animation for smooth reordering (drag/drop swap)
const animateReorder = async (fromIndex, toIndex) => {
  const slots = document.querySelectorAll('.card-slot');
  const fromSlot = slots[fromIndex];
  const toSlot = slots[toIndex];
  
  if (!fromSlot || !toSlot) return;
  
  // Get the cards in both slots BEFORE any changes
  const fromCard = fromSlot.querySelector('.card');
  const toCard = toSlot.querySelector('.card');
  
  if (!fromCard) return;
  
  // Store initial positions (First)
  const fromRect = fromCard.getBoundingClientRect();
  const toRect = toCard ? toCard.getBoundingClientRect() : toSlot.getBoundingClientRect();
  
  // Immediately perform the swap in the DOM (Last)
  const newCards = [...gameStore.cards];
  const draggedCard = newCards[fromIndex];
  const targetCard = newCards[toIndex];
  newCards[fromIndex] = targetCard;
  newCards[toIndex] = draggedCard;
  gameStore.updateCards(newCards);
  
  // Wait for DOM update
  await nextTick();
  
  // Get new positions after swap (Invert)
  const newFromCard = fromSlot.querySelector('.card');
  const newToCard = toSlot.querySelector('.card');
  
  if (newFromCard) {
    const newFromRect = newFromCard.getBoundingClientRect();
    const deltaX = fromRect.left - newFromRect.left;
    const deltaY = fromRect.top - newFromRect.top;
    
    // Apply inverse transform to make it appear in old position
    newFromCard.style.transform = `translate(${deltaX}px, ${deltaY}px)`;
    newFromCard.style.transition = 'none';
  }
  
  if (newToCard) {
    const newToRect = newToCard.getBoundingClientRect();
    const deltaX = toRect.left - newToRect.left;
    const deltaY = toRect.top - newToRect.top;
    
    // Apply inverse transform to make it appear in old position
    newToCard.style.transform = `translate(${deltaX}px, ${deltaY}px)`;
    newToCard.style.transition = 'none';
  }
  
  // Force reflow
  if (newFromCard) newFromCard.offsetHeight;
  if (newToCard) newToCard.offsetHeight;
  
  // Play the animation (Play)
  return new Promise(resolve => {
    if (newFromCard) {
      newFromCard.style.transition = 'transform 0.4s cubic-bezier(0.4, 0.0, 0.2, 1)';
      newFromCard.style.transform = 'translate(0, 0)';
    }
    
    if (newToCard) {
      newToCard.style.transition = 'transform 0.4s cubic-bezier(0.4, 0.0, 0.2, 1)';
      newToCard.style.transform = 'translate(0, 0)';
    }
    
    setTimeout(() => {
      // Clean up
      if (newFromCard) {
        newFromCard.style.transform = '';
        newFromCard.style.transition = '';
      }
      if (newToCard) {
        newToCard.style.transform = '';
        newToCard.style.transition = '';
      }
      resolve();
    }, 400);
  });
};

/**
 * Horizontal reshuffle: measure current slot positions, apply target hand order,
 * invert with transforms, then animate to the new layout (FLIP).
 */
const animateFullHandShuffle = async (targetHand) => {
  const slots = document.querySelectorAll('#cards-container .card-slot');
  const firstRects = new Map();

  gameStore.cards.forEach((card, index) => {
    if (!card) return;
    const el = slots[index]?.querySelector('.card');
    if (el) firstRects.set(card.id, el.getBoundingClientRect());
  });

  gameStore.updateCards(targetHand);
  await nextTick();

  const moving = [];
  targetHand.forEach((card, index) => {
    if (!card) return;
    const el = slots[index]?.querySelector('.card');
    const first = firstRects.get(card.id);
    if (!el || !first) return;

    const last = el.getBoundingClientRect();
    const dx = first.left - last.left;
    const dy = first.top - last.top;
    if (Math.abs(dx) < 1 && Math.abs(dy) < 1) return;

    el.style.transition = 'none';
    el.style.transform = `translate(${dx}px, ${dy}px)`;
    el.classList.add('card-shuffle-moving');
    moving.push(el);
  });

  moving.forEach((el) => el.offsetHeight);

  await new Promise((resolve) => {
    moving.forEach((el) => {
      el.style.transition = 'transform 0.55s cubic-bezier(0.4, 0, 0.2, 1)';
      el.style.transform = 'translate(0, 0)';
    });
    setTimeout(() => {
      moving.forEach((el) => {
        el.style.transform = '';
        el.style.transition = '';
        el.classList.remove('card-shuffle-moving');
      });
      resolve();
    }, 560);
  });

  gameStore.completeDealShuffle();
};

watch(
  () => gameStore.pendingShuffleTo,
  async (target) => {
    if (!target || !target.length) return;
    await animateFullHandShuffle(target);
  }
);

// Expose the selectedCards from the store
const selectedCards = computed(() => gameStore.selectedCards);

// Toggle selection when a card is clicked
const handleSelect = (card) => {
  gameStore.toggleSelectedCard(card);
  // If exactly 3 cards are selected, move them to jobsDone automatically
  if (gameStore.selectedCards.length === 3) {
    gameStore.moveSelectedCardsToJobsDone();
  }
};

const emit = defineEmits(['show-explanation']);

// Handle drag start: set drag data as a JSON object under "text/plain"
// External drop dragstart (in CardContainer.vue)
const handleDragStart = (event, index) => {
  event.dataTransfer.effectAllowed = "move";
  const card = gameStore.cards[index];
  if (!card) return;
  
  // Set the moving card ID
  movingCardId.value = card.id;
  
  // When allowJobDrop is true (Check Order active), mark as external:
  if (gameStore.allowJobDrop) {
    const dragData = { source: "external", card: card };
    event.dataTransfer.setData("text/plain", JSON.stringify(dragData));
  } else {
    // For internal reordering in CardContainer:
    const dragData = { source: "internal", index: index, card: card };
    event.dataTransfer.setData("text/plain", JSON.stringify(dragData));
  }
  console.log("CardContainer: Drag data set:", JSON.parse(event.dataTransfer.getData("text/plain")));
};

const handleDragOver = (event) => {
  event.preventDefault();
};

const handleDrop = (event, dropIndex) => {
  event.preventDefault();
  console.log("CardContainer: Drop triggered at index:", dropIndex);

  // Retrieve drag data from "text/plain"
  const dragDataStr = event.dataTransfer.getData("text/plain");
  if (!dragDataStr) {
    console.error("CardContainer: No drag data found");
    return;
  }
  let dragData;
  try {
    dragData = JSON.parse(dragDataStr);
  } catch (error) {
    console.error("CardContainer: Error parsing drag data:", error);
    return;
  }
  console.log("CardContainer: Parsed drag data:", dragData);

  // We only handle internal reordering here
  if (dragData.source !== "internal") {
    console.warn("CardContainer: Drop ignored: drag source is not internal.");
    return;
  }
  
  const draggedIndex = Number(dragData.index);
  if (isNaN(draggedIndex)) {
    console.error("CardContainer: Invalid dragged index");
    return;
  }
  if (draggedIndex === dropIndex) {
    console.warn("CardContainer: Dropped on the same slot. No move needed.");
    return;
  }
  
  console.log("CardContainer: Moving card from", draggedIndex, "to", dropIndex);
  
  // Trigger smooth animation (swap is handled inside animateReorder)
  animateReorder(draggedIndex, dropIndex).then(() => {
    // Clear the moving card ID after animation completes
    movingCardId.value = null;
  });
};

const handleShowExplanation = (explanation) => {
  // Forward the event upward to HomeView
  emit("show-explanation", explanation);
};
</script>

<style src="../styles/_cardContainer.scss" lang="scss" scoped></style>